import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

export interface IInvoice {
  invoiceId: string;
  systemId: string;
  invoiceDate: string;
  subscriptionId: string;
  total: string;
  imageCapturing: string;
  captureVideo: string;
  gemPassportProduction: string;
  gemPassportLiteProduction: string;
  verifyGemPassport: string;
  status: string;
}

export interface IPayment {
  paymentId: string;
  stripePaymentId: string | null;
  amount: string;
  paymentStatus: string;
  description: string;
  invoice: IInvoice | null;
}

export interface IPaymentSlice {
  componentStatus: { [name: string]: undefined | string | boolean };
  payments: IPayment[] | null;
  error: string | null;
  portalUrl: string | null;
  paymentMethodsLoading: boolean;
  paymentError: string | null;
  paymentSuccess: boolean;
}

const initialState: IPaymentSlice = {
  componentStatus: {},
  payments: null,
  error: null,
  portalUrl: null,
  paymentMethodsLoading: false,
  paymentError: null,
  paymentSuccess: false,
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    setComponentStatus(state: IPaymentSlice, action: PayloadAction<{ [key: string]: undefined | string | boolean }>) {
      state.componentStatus = { ...state.componentStatus, ...action.payload };
    },
    clearComponentStatus(state: IPaymentSlice, action: PayloadAction<string[] | undefined>) {
      if (action.payload?.length) {
        state.componentStatus = _.omit(state.componentStatus, action.payload);
      } else {
        state.componentStatus = {};
      }
    },
    getPaymentsByOrganizationIdAsync(_state: IPaymentSlice, _action: PayloadAction<string>) {},
    setPayments(state: IPaymentSlice, action: PayloadAction<IPayment[]>) {
      state.payments = action.payload;
    },
    fetchPaymentsByOrganizationIdStart(state: IPaymentSlice) {
      state.error = null;
    },
    fetchPaymentsByOrganizationIdSuccess(state: IPaymentSlice, action: PayloadAction<IPayment[]>) {
      state.payments = action.payload;
      state.error = null;
    },
    fetchPaymentsByOrganizationIdFailure(state: IPaymentSlice, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    retryPaymentAsync(
      _state,
      _action: PayloadAction<{ stripeAccountId: string; amount: number; paymentId: string }>,
    ) {},
    setPortalUrl(state: IPaymentSlice, action: PayloadAction<string | null>) {
      state.portalUrl = action.payload;
      state.paymentMethodsLoading = false;
    },
    createPortalSessionAsync(state, _action: PayloadAction<{ stripeAccountId: string; returnUrl: string }>) {
      state.paymentMethodsLoading = true;
    },
    setPaymentError(state: IPaymentSlice, action: PayloadAction<string>) {
      state.paymentError = action.payload;
    },
    clearPaymentError(state: IPaymentSlice) {
      state.paymentError = null;
    },
    retryPaymentSuccess(state: IPaymentSlice) {
      state.paymentSuccess = true;
    },
    clearPaymentSuccess(state: IPaymentSlice) {
      state.paymentSuccess = false;
    },
  },
});

export const {
  setComponentStatus,
  clearComponentStatus,
  fetchPaymentsByOrganizationIdStart,
  fetchPaymentsByOrganizationIdSuccess,
  fetchPaymentsByOrganizationIdFailure,
  retryPaymentAsync,
  setPortalUrl,
  createPortalSessionAsync,
} = paymentSlice.actions;

export const { actions: paymentActions, reducer: paymentReducer } = paymentSlice;
